import axios from 'axios'
import { API_COLLECTOR_URL, API_JPOOL_URL, API_URL, BUILD_VERSION } from '~/config'

axios.interceptors.request.use((config) => {
  config.params = { ...config.params, build: BUILD_VERSION }
  return config
}, (error) => {
  return Promise.reject(error)
})

export type ValidatorStats = {
  voteId: string
  validatorId: string
  fee: number
  apy: number
  apyEst: number
  totalStake: number | string
  network: string
  name: string | undefined
  details: string | undefined
  website: string | undefined
  keybaseUsername: string | undefined
  iconUrl: string | undefined
  inTop33: boolean
  inJpool: boolean
  isDelinquent: boolean
  svName: string
  apyComparedMax: number
  jpoolLamports: number | string
  nodeVersion: string
  lastVote: string
  leaderSlotsTotal: number
  leaderSlotsDone: number
  leaderSlotsEpoch: number
}

export type DcData = {
  asn: number
  aso: string
  city?: string
  continent: string
  coordinates: [number, number]
  country: string
  country_iso: string
}

export type StakeConcentration = {
  dc: number
  asn: number
  city: number
  country: number
}

export type ScoreInfo = {
  apy3_score: number
  apy10_score: number
  apy_score: number
  asn_concentration_score: number
  city_concentration_score: number
  commission_score: number
  country_concentration_score: number
  dc_concentration_score: number
  info_score: number
  invalid_version_penalty: number
  jito_score: number
  skip_rate_score: number
  stake_weight_score: number
  superminority_penalty: number
  svt_member_score: number
  uptime_score: number
  vote_success_score: number
  withdraw_authority_score: number
}

// new api data
export type ValidatorInfo = {
  activated_stake: number
  apy: number
  blocks_produced: number
  comment?: string
  commission: number
  created_at: string
  credits: number
  data_center?: DcData
  delinquent: boolean
  details?: string
  downtime: number
  epoch: number
  identity: string
  image?: string
  is_jito: boolean
  is_jpool: boolean
  is_poor_performer: boolean
  is_svt: boolean
  jito_apy: number
  jito_commission_bps: number
  jpool_score: number
  jpool_stake: number
  keybase?: string
  leader_slots: number
  name?: string
  node_ip?: string
  score_info: ScoreInfo
  skip_rate: number
  stake_concentration: StakeConcentration
  stake_to_become_superminority: number
  stake_weight: number
  superminority: boolean
  tpu_ip: string
  updated_at: string
  uptime: number
  version: string
  vote_account: string
  website?: string
  wiz_score: number
}

type CoinStats = {
  ath: number
  ath_change_percentage: number
  ath_date: string
  atl: number
  atl_change_percentage: number
  atl_date: string
  circulating_supply: number
  current_price: number
  fully_diluted_valuation: null
  high_24h: number
  id: string
  image: string
  last_updated: string
  low_24h: number
  market_cap: number
  market_cap_change_24h: number
  market_cap_change_percentage_24h: number
  market_cap_rank: number
  // max_supply: null,
  name: string
  price_change_24h: number
  price_change_percentage_24h: number
  price_change_percentage_24h_in_currency: number
  // roi: null,
  symbol: string
  total_supply: number
  total_volume: number
}

export type EpochInfo = {
  epoch: number
  network?: string
  absoluteSlot: number
  blockHeight?: number
  hourlySlotTime?: number
  slotIndex: number
  slotsInEpoch: number
  transactionCount?: number
}

type AprTvl = {
  apr?: number
  tvl?: number
}

export type DefiValues = Record<string, AprTvl>

export type JpoolScore = {
  voteId: string
  isJpoolValidator: boolean
  jpoolScore: number
  jpoolRank: number
  svtMemberScore: number
  vaTotalScore: number
  vaStakePercentScore: number
  vaPublishedInformationScore: number
  apyScore: number
  apy3Score: number
  apy10Score: number
  scAsnScore: number
  scDcScore: number
  scCountryScore: number
  scCityScore: number
  publishedInformationScore: number
  desiredStake: string
  dropReason: string
}

export type ValidatorStakeInfo = {
  epoch: number
  vote_account: string
  stake_type: string
  stake_amount: number
  createdAt: string
}

export async function getValidators(network: string): Promise<Array<ValidatorStats>> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/validators/list?network=${network}&select=voteId,validatorId,fee,apy,apyEst,totalStake,name,details,website,keybaseUsername,iconUrl,inTop33,inJpool,isDelinquent,svName,apyComparedMax,jpoolLamports,nodeVersion,lastVote,leaderSlotsTotal,leaderSlotsDone,leaderSlotsEpoch`,
  )
  return resp.data.data
}

export async function getValidatorsInfo(): Promise<Array<ValidatorInfo>> {
  const resp = await axios.get(
    `${API_URL}/validators?fields=apy,jito_apy,stake_concentration,data_center,node_ip`,
  )
  return resp.data
}

export async function getCoinStats(): Promise<Record<string, CoinStats>> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/coins`,
  )
  return resp.data.data
}

export async function getEpochInfo(network: string): Promise<EpochInfo> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/current-epochs/${network}`,
  )
  return resp.data.data
}

export async function getWithdrawList(): Promise<Record<string, number>> {
  const resp = await axios.get(
    `${API_JPOOL_URL}/withdrawal-list`,
  )
  const data = resp.data as Array<{ voteId: string, stake: number }>
  return data.reduce((map: any, item) => {
    map[item.voteId] = item.stake
    return map
  }, {})
}

export type StakePoolFees = {
  epochFee: number
  stakeDepositFee: number
  solDepositFee: number
  withdrawalFee: number
  solWithdrawalFee: number
  nextStakeWithdrawalFee: number
  nextSolWithdrawalFee: number
  nextEpochFee: number
  solReferralFee: number
  stakeReferralFee: number
}

export type StakePoolData = {
  poolAddress: string
  poolMint: string
  reserveStakeAccount: string
  poolTokenSupply: number
  totalLamports: number
  exchangeRate: number
  fees: StakePoolFees
  reserveStakeBalance: number
  minRentBalance: number
  connectionLost: boolean
}

export async function getValidatorsJpoolScores(epoch: number): Promise<JpoolScore[]> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/jpool-scores/${epoch}?select=voteId,jpoolScore,jpoolRank,svtMemberScore,desiredStake,dropReason,vaTotalScore,vaStakeConcentrationScore,vaPublishedInformationScore,apyScore,apy3Score,apy10Score,scAsnScore,scDcScore,scCountryScore,scCityScore,publishedInformationScore`,
  )
  return resp.data.data
}

// Stakes
export async function checkWalletInDelegators(wallet: string) {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/delegator-wallets/${wallet}`,
  )
  return resp.data
}

export async function addToDelegators(wallet: string, network = 'mainnet') {
  const resp = await axios.post(
    `${API_COLLECTOR_URL}/delegator-wallets/${wallet}${network ? `?network=${network}` : ''}`,
  )
  return resp.data
}

export async function getWalletRewards(wallet: string, network = 'mainnet') {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/stake-accounts/list?wallet=${wallet}&network=${network}`,
  )
  return resp.data
}

export async function getStakesStatus(wallet: string, network = 'mainnet') {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/stake-accounts/checkCompleteness?wallet=${wallet}&network=${network}`,
  )
  return resp.data
}

// Validator page api
export type GetValidatorHistoryProps = {
  voteKey?: string
  identity?: string
  network?: string
  epochFrom?: number
  epochCount?: number
}

export type ApyStats = {
  apy: number
  epoch: number
}

export type ValidatorHistory = {
  apy: number
  epoch: number
  fee: number
  totalStake: number
  commissionReward: number | string
  votingReward: number | string
  votingFee: number | string
  votingCompensation: number | string
  jitoReward: number | string
  skippedSlots: number
  leaderSlotsDone: number
  leaderSlotsTotal: number
}

export type EpochData = {
  id: number
  epoch: number
  network: string
  firstSlot: number
  lastSlot: number
  firstBlock: number
  lastBlock: number | null
  startTime: string
  endTime: string
  createdAt: string
}

export type ValidatorVotingStats = {
  validatorId: string
  voteId: string
  // network: string
  epoch: number
  identityBalance: number | string
  isDelinquent: boolean
  epochCredits: number
  slotIndex: number
  leaderSlotsTotal: number
  leaderSlotsDone: number
  createdAt: string
}

export type MeanSlot = {
  createdAt: string
  epoch: number
  id: number
  leaderSlotsDone: number
  skippedSlots: number
  network: number
  slotIndex: number
}

/**
 * cluster leader slots history by epochs
 */
export async function getAvgSlotsHistory(network = 'mainnet', epoch?: number): Promise<Array<MeanSlot>> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/mean-slots-data/history?network=${network}&epoch_count=30${epoch ? `&epoch_from=${epoch}` : ''}`,
  )
  return resp.data.data
}

/**
 * cluster leader slots for 24h by 10min interval
 */
export async function getAvgSlotsCurrent(network = 'mainnet', days = 1): Promise<Array<MeanSlot>> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/mean-slots-data/?network=${network}&days=${days}`,
  )
  return resp.data.data
}

export async function getAverageApy(): Promise<Array<ApyStats>> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/apy-average/history`,
  )
  return resp.data.data
}

export type CoinHistory = {
  name: string
  close: number
  epoch: number
}

export type ClusterInfo = {
  created_at: string
  epoch: number
  min_superminority_stake: number
  nakamoto_coefficient: number
  total_activating_stake: number
  total_deactivation_stake: number
  total_effective_stake: number
  total_nodes: number
  total_stakers: number
  total_validators: number
  unique_asns: number
  unique_cities: number
  unique_countries: number
  unique_dcs: number
  unique_stakers: number
  unique_voters: number
}
